import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import AppHeader from "../components/AppHeader";
import DisclaimerBanner from "../components/DisclaimerBanner";
import SiteFooter from "../components/SiteFooter";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { Shield } from "lucide-react";

const inputCls =
  "rounded-none border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

export default function MfaVerify() {
  const { checkAuth } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);

  const returnTo = new URLSearchParams(loc.search).get("return_to") || "/dashboard";

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/mfa/verify", { code: code.trim() });
      toast.success("Verified");
      await checkAuth();
      nav(returnTo);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <DisclaimerBanner />
      <AppHeader />
      <main className="max-w-[560px] mx-auto px-6 py-16 md:py-24 flex-1 w-full" data-testid="mfa-verify-page">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-slate-100 text-slate-800 mb-4">
          <Shield strokeWidth={1.75} className="w-6 h-6" />
        </div>
        <h1 className="font-serif-display text-3xl md:text-4xl text-slate-900 leading-tight">
          Verify it&apos;s you
        </h1>
        <p className="mt-3 text-slate-600 text-sm">
          Enter the 6-digit code from your authenticator app. Lost your device? Use a recovery code instead.
        </p>

        <div className="mt-8 space-y-4">
          <div>
            <Label className="label-tiny mb-2 block">
              {useRecovery ? "Recovery code" : "Authenticator code"}
            </Label>
            <Input
              value={code}
              onChange={(e) => setCode(useRecovery ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode={useRecovery ? "text" : "numeric"}
              maxLength={useRecovery ? 20 : 6}
              autoComplete="one-time-code"
              placeholder={useRecovery ? "xxxx-xxxx-xx" : "123456"}
              className={`${inputCls} font-mono-tech tracking-widest text-lg`}
              data-testid="mfa-verify-input"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { setUseRecovery(!useRecovery); setCode(""); }}
              className="rounded-none text-slate-600 hover:text-slate-900"
              data-testid="toggle-recovery-mode-btn"
            >
              {useRecovery ? "Use authenticator code" : "Use a recovery code"}
            </Button>
            <Button
              onClick={submit}
              disabled={busy || code.length < (useRecovery ? 10 : 6)}
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="mfa-verify-submit-btn"
            >
              {busy ? "Verifying…" : "Verify"}
            </Button>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
